import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import {
  Globe, Sparkles, Copy, ExternalLink, Save, Plus, Trash2,
  Star, Briefcase, Lock, Upload, X, Image as ImageIcon, Car,
} from "lucide-react";
import { toast } from "sonner";
import { QRShareCard } from "../components/QRShareCard";
import { absUrl } from "../lib/seo";

const SECTIONS = [
  ["hero",         "Hero"],
  ["about",        "About"],
  ["vehicles",     "Vehicles"],
  ["how_it_works", "How booking works"],
  ["policies",     "Policies"],
  ["faq",          "FAQ"],
  ["reviews",      "Reviews"],
  ["contact",      "Contact"],
  ["trust",        "Trust badge"],
];

const POLICY_FIELDS = [
  ["deposit", "Deposit policy"],
  ["cancellation", "Cancellation policy"],
  ["late_return", "Late return policy"],
  ["fuel", "Fuel policy"],
  ["damage", "Damage policy"],
  ["cleaning", "Cleaning policy"],
  ["extension", "Extension policy"],
  ["delivery", "Pickup / delivery policy"],
  ["travel", "Travel area policy"],
  ["refund", "Refund policy"],
];

export default function PublicWebsiteSettings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [slugDraft, setSlugDraft] = useState("");
  const [slugStatus, setSlugStatus] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [uploadingHero, setUploadingHero] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/business/website");
      setData(data);
      setSlugDraft(data.public_slug || "");
      const vehiclesRes = await api.get("/vehicles");
      setVehicles(vehiclesRes.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, [load]);

  if (!data) return <div className="p-6 text-slate-500">Loading…</div>;
  const isEnterprise = !!data.enterprise;

  if (!isEnterprise) return <WebsiteLockedScreen plan={data.plan_type} />;

  const patch = (k, v) => setData((p) => ({ ...p, [k]: v }));
  const patchSection = (key, val) =>
    setData((p) => ({ ...p, website_sections_enabled: { ...p.website_sections_enabled, [key]: val } }));
  const patchPolicy = (key, val) =>
    setData((p) => ({ ...p, website_policies: { ...p.website_policies, [key]: val } }));

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const payload = { ...extra };
      // Only include fields that changed visibly, for simplicity send the whole editable set
      const f = data;
      Object.assign(payload, {
        public_website_enabled: f.public_website_enabled,
        website_headline: f.website_headline,
        website_intro: f.website_intro,
        website_about: f.website_about,
        website_why_choose_us: f.website_why_choose_us,
        website_service_area_text: f.website_service_area_text,
        website_pickup_delivery_notes: f.website_pickup_delivery_notes,
        website_theme_color: f.website_theme_color,
        website_sections_enabled: f.website_sections_enabled,
        website_policies: f.website_policies,
        website_faq_items: f.website_faq_items,
        main_cta_type: f.main_cta_type,
        show_prices: f.show_prices, show_reviews: f.show_reviews,
        show_policies: f.show_policies, show_faq: f.show_faq,
        show_business_hours: f.show_business_hours, show_contact_details: f.show_contact_details,
      });
      const { data: saved } = await api.put("/business/website", payload);
      setData(saved);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const checkSlug = async () => {
    if (!slugDraft.trim()) return setSlugStatus(null);
    try {
      const { data } = await api.get("/business/website/slug-check", { params: { slug: slugDraft } });
      setSlugStatus(data);
    } catch (e) { setSlugStatus({ ok: false, message: formatApiError(e) }); }
  };

  const saveSlug = async () => {
    if (!isEnterprise) return toast.error("Enterprise plan required");
    setBusy(true);
    try {
      const { data: saved } = await api.put("/business/website", { public_slug: slugDraft });
      setData(saved); toast.success("Slug saved");
      setSlugStatus({ ok: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}/rentals/${data.public_slug || data.company_id}`;
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  const requestUpsell = async (kind) => {
    try {
      const { data: saved } = await api.post("/business/website/upsell", { kind });
      setData(saved);
      toast.success(kind === "custom_website"
        ? "Custom website request sent: we'll contact you."
        : "Premium visibility request recorded: admin will activate.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addFaq = () => patch("website_faq_items",
    [...(data.website_faq_items || []),
     { id: `tmp-${Date.now()}`, question: "", answer: "", active: true,
       order: (data.website_faq_items || []).length }]);

  const uploadHeroImage = async (file) => {
    if (!file) return;
    setUploadingHero(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data: uploadData } = await api.post("/business/website/upload/hero-image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      patch("website_hero_image_url", uploadData.url);
      toast.success("Hero image uploaded");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploadingHero(false);
    }
  };

  const deleteHeroImage = async () => {
    try {
      await api.delete("/business/website/upload/hero-image");
      patch("website_hero_image_url", null);
      toast.success("Hero image deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleFeaturedVehicle = (vehicleId) => {
    const current = data.website_featured_vehicle_ids || [];
    const newList = current.includes(vehicleId)
      ? current.filter(id => id !== vehicleId)
      : current.length < 6
        ? [...current, vehicleId]
        : current;
    patch("website_featured_vehicle_ids", newList);
  };

  const activate = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/business/website/activate");
      setData(r);
      toast.success("Website activated", {
        description: "Your public mini-site is now live. Missing fields were filled with safe defaults.",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };
  const deactivate = async () => {
    if (!window.confirm("Deactivate your public website? Your content stays saved: you can re-activate any time.")) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/business/website/deactivate");
      setData(r);
      toast.success("Website deactivated. Content is saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const updateFaq = (idx, k, v) => {
    const next = [...data.website_faq_items];
    next[idx] = { ...next[idx], [k]: v };
    patch("website_faq_items", next);
  };
  const removeFaq = (idx) => {
    const next = data.website_faq_items.filter((_, i) => i !== idx);
    patch("website_faq_items", next);
  };

  return (
    <div className="space-y-6" data-testid="public-website-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold inline-flex items-center gap-2">
            <Globe className="w-5 h-5 text-[#1B4332]" /> Public booking website
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Shape how customers see your business when you share your ConfirmPH link.
          </p>
        </div>
        <a href={`/rentals/${data.public_slug || data.company_id}`} target="_blank" rel="noreferrer"
           data-testid="pw-preview"
           className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-sm">
          <ExternalLink className="w-3.5 h-3.5" /> Preview
        </a>
      </div>

      {/* Activation banner: Enterprise only (non-Enterprise are intercepted above). */}
      <div
        className={`rounded-xl border-2 p-4 flex flex-wrap items-start gap-4 ${
          data.public_website_enabled
            ? "border-emerald-300 bg-emerald-50"
            : "border-amber-300 bg-amber-50"
        }`}
        data-testid="website-activation-banner"
      >
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold uppercase tracking-widest">
            Website status
          </div>
          <div className="text-lg font-display font-semibold mt-0.5" data-testid="website-status-label">
            {data.public_website_enabled ? "Active · public mini-site is live" : "Inactive · multi-page website is off"}
          </div>
          <p className="text-xs text-slate-700 mt-1 max-w-2xl">
            {data.public_website_enabled
              ? "Your shareable link opens the full multi-page website. Deactivating keeps every saved field: you can turn it back on any time."
              : "Activate to publish your multi-page website. The first activation fills any blank fields with safe defaults: your existing edits are never overwritten."}
          </p>
        </div>
        {data.public_website_enabled ? (
          <button onClick={deactivate} disabled={busy} data-testid="website-deactivate-btn"
                  className="px-4 py-2 rounded-lg border border-amber-400 bg-white text-amber-800 text-sm hover:bg-amber-100 disabled:opacity-50">
            Deactivate
          </button>
        ) : (
          <button onClick={activate} disabled={busy} data-testid="website-activate-btn"
                  className="px-5 py-2.5 rounded-lg bg-[#1B4332] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {busy ? "Activating…" : "Activate website"}
          </button>
        )}
      </div>

      <ProfileHealthCard />


      {!isEnterprise && (
        <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
             data-testid="pw-enterprise-required">
          <div className="font-semibold inline-flex items-center gap-1">
            <Lock className="w-4 h-4" /> The upgraded mini-site is an Enterprise feature
          </div>
          <p className="mt-1">
            Free, Basic, and Pro plans keep the standard listing page. Upgrade to Enterprise to unlock
            the editable booking-website experience.
          </p>
          <Link to="/app/billing" className="mt-2 inline-block text-amber-900 underline">View pricing →</Link>
        </div>
      )}

      {/* Shareable link */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="pw-slug-card">
        <h2 className="font-display text-lg font-semibold">Your ConfirmPH booking website link</h2>
        <div className="flex flex-wrap items-center gap-2 mt-3 text-sm">
          <span className="text-slate-500">{window.location.origin}/rentals/</span>
          <input type="text" value={slugDraft}
                 onChange={(e) => { setSlugDraft(e.target.value); setSlugStatus(null); }}
                 onBlur={checkSlug}
                 disabled={!isEnterprise}
                 placeholder="your-business-slug"
                 data-testid="pw-slug-input"
                 className="rounded-md border border-slate-300 px-2 py-1 font-mono disabled:bg-slate-100" />
          <button onClick={saveSlug} disabled={!isEnterprise || busy}
                  data-testid="pw-slug-save"
                  className="px-3 py-1.5 rounded-md bg-[#1B4332] text-white text-xs disabled:opacity-50">Save</button>
          <button onClick={copyLink}
                  data-testid="pw-copy-link"
                  className="px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-xs inline-flex items-center gap-1">
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
        {slugStatus && (
          <div className={`mt-2 text-xs ${slugStatus.ok ? "text-emerald-700" : "text-red-700"}`}
               data-testid="pw-slug-status">
            {slugStatus.ok ? `Available: ${slugStatus.slug || slugDraft.toLowerCase()}`
              : (slugStatus.message || "Slug invalid or taken")}
          </div>
        )}
        <p className="text-xs text-slate-500 mt-2">
          Lowercase letters, numbers, and hyphens only. Some words like &quot;admin&quot;, &quot;app&quot;, &quot;rentals&quot; are reserved.
        </p>
      </div>

      {/* QR share: canonical mini-site link */}
      <div data-testid="pw-share-section">
        <QRShareCard
          url={absUrl(`/rentals/${data.public_slug || data.company_id}`)}
          label="Share your booking website with a QR code"
          description="Print this on tarpaulins, flyers, or business cards. Customers scan and land on your live mini-site: no typing, no SMS link."
          filename={`confirmph-${data.public_slug || data.company_id}`}
          testIdPrefix="pw-qr-share"
        />
      </div>

      {/* Main fields */}
      <Card title="Main content" testId="pw-main">
        <Toggle label="Public website enabled" checked={!!data.public_website_enabled}
                onChange={(v) => patch("public_website_enabled", v)}
                disabled={!isEnterprise} testId="pw-enable" />
        <Text label="Headline / tagline" value={data.website_headline || ""}
              onChange={(v) => patch("website_headline", v)} testId="pw-headline" disabled={!isEnterprise} />
        <Area label="Short intro" rows={2} value={data.website_intro || ""}
              onChange={(v) => patch("website_intro", v)} testId="pw-intro" disabled={!isEnterprise} />
        <Area label="About us" rows={5} value={data.website_about || ""}
              onChange={(v) => patch("website_about", v)} testId="pw-about" disabled={!isEnterprise} />

        {/* Hero Image Upload */}
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-widest text-slate-500">Hero cover image</label>
          {data.website_hero_image_url ? (
            <div className="relative rounded-lg border border-slate-200 overflow-hidden">
              <img
                src={data.website_hero_image_url}
                alt="Hero cover"
                className="w-full h-48 object-cover"
              />
              <button
                onClick={deleteHeroImage}
                disabled={!isEnterprise}
                className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center">
              <ImageIcon className="w-12 h-12 mx-auto text-slate-400 mb-3" />
              <p className="text-sm text-slate-600 mb-3">Upload a cover image for your mini-site</p>
              <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer disabled:opacity-50">
                <Upload className="w-4 h-4" />
                {uploadingHero ? "Uploading..." : "Choose Image"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadHeroImage(e.target.files[0])}
                  disabled={!isEnterprise || uploadingHero}
                  className="hidden"
                />
              </label>
              <p className="text-xs text-slate-500 mt-2">Max 10MB • JPG, PNG, WebP, GIF</p>
            </div>
          )}
        </div>

        {/* Featured Vehicles */}
        <div className="space-y-2">
          <label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
            <Car className="w-4 h-4" />
            Featured vehicles (max 6)
          </label>
          <div className="text-xs text-slate-600 mb-2">
            Select up to 6 vehicles to highlight on your mini-site
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {vehicles.map((vehicle) => {
              const isSelected = (data.website_featured_vehicle_ids || []).includes(vehicle.id);
              const canSelect = (data.website_featured_vehicle_ids || []).length < 6 || isSelected;
              return (
                <button
                  key={vehicle.id}
                  onClick={() => toggleFeaturedVehicle(vehicle.id)}
                  disabled={!isEnterprise || !canSelect}
                  className={`text-left p-3 rounded-lg border-2 transition-all disabled:opacity-50 ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      readOnly
                      className="rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{vehicle.name}</div>
                      <div className="text-xs text-slate-600">
                        {vehicle.category} • ₱{vehicle.price_per_day}/day
                      </div>
                    </div>
                    {isSelected && <Star className="w-4 h-4 text-blue-600 flex-shrink-0" />}
                  </div>
                </button>
              );
            })}
          </div>
          {vehicles.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-4 border border-slate-200 rounded-lg">
              No vehicles found. Add vehicles first to feature them here.
            </div>
          )}
        </div>

        <Area label="Why customers choose us" rows={3} value={data.website_why_choose_us || ""}
              onChange={(v) => patch("website_why_choose_us", v)} testId="pw-why" disabled={!isEnterprise} />
        <Area label="Service area" rows={2} value={data.website_service_area_text || ""}
              onChange={(v) => patch("website_service_area_text", v)} testId="pw-area" disabled={!isEnterprise} />
        <Area label="Pickup / delivery notes" rows={2} value={data.website_pickup_delivery_notes || ""}
              onChange={(v) => patch("website_pickup_delivery_notes", v)} testId="pw-pickup" disabled={!isEnterprise} />
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs uppercase tracking-widest text-slate-500">Theme color</label>
          <input type="color" value={data.website_theme_color || "#1B4332"}
                 onChange={(e) => patch("website_theme_color", e.target.value)}
                 disabled={!isEnterprise} data-testid="pw-theme-color"
                 className="w-12 h-8 rounded border border-slate-300" />
          <span className="text-xs text-slate-600 font-mono">{data.website_theme_color}</span>
        </div>
      </Card>

      {/* Sections */}
      <Card title="Visible sections" testId="pw-sections">
        <div className="grid sm:grid-cols-3 gap-2">
          {SECTIONS.map(([k, l]) => (
            <Toggle key={k} label={l} checked={!!data.website_sections_enabled?.[k]}
                    onChange={(v) => patchSection(k, v)}
                    disabled={!isEnterprise} testId={`pw-section-${k}`} />
          ))}
        </div>
      </Card>

      {/* Policies */}
      <Card title="Policies" testId="pw-policies">
        {POLICY_FIELDS.map(([k, l]) => (
          <Area key={k} label={l} rows={2}
                value={data.website_policies?.[k] || ""}
                onChange={(v) => patchPolicy(k, v)}
                testId={`pw-policy-${k}`} disabled={!isEnterprise} />
        ))}
      </Card>

      {/* FAQ */}
      <Card title="Frequently asked questions" testId="pw-faq">
        {(data.website_faq_items || []).map((f, i) => (
          <div key={f.id || i} className="rounded-lg border border-slate-200 p-3 space-y-2"
               data-testid={`pw-faq-${i}`}>
            <input type="text" value={f.question} placeholder="Question"
                   onChange={(e) => updateFaq(i, "question", e.target.value)}
                   disabled={!isEnterprise}
                   data-testid={`pw-faq-q-${i}`}
                   className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100" />
            <textarea rows={3} value={f.answer} placeholder="Answer"
                      onChange={(e) => updateFaq(i, "answer", e.target.value)}
                      disabled={!isEnterprise}
                      data-testid={`pw-faq-a-${i}`}
                      className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm disabled:bg-slate-100" />
            <div className="flex justify-between text-xs">
              <label className="inline-flex items-center gap-1">
                <input type="checkbox" checked={!!f.active}
                       onChange={(e) => updateFaq(i, "active", e.target.checked)}
                       disabled={!isEnterprise}
                       data-testid={`pw-faq-active-${i}`} /> Active
              </label>
              <button onClick={() => removeFaq(i)} type="button" disabled={!isEnterprise}
                      data-testid={`pw-faq-remove-${i}`}
                      className="text-red-700 hover:underline disabled:opacity-50 inline-flex items-center gap-1">
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          </div>
        ))}
        <button onClick={addFaq} disabled={!isEnterprise} type="button"
                data-testid="pw-faq-add"
                className="px-3 py-1.5 rounded-md border border-slate-300 bg-white hover:bg-slate-50 text-sm inline-flex items-center gap-1 disabled:opacity-50">
          <Plus className="w-3 h-3" /> Add FAQ
        </button>
      </Card>

      {/* Save bar */}
      <div className="flex justify-end gap-2 sticky bottom-3">
        <button onClick={() => save()} disabled={!isEnterprise || busy}
                data-testid="pw-save"
                className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1 shadow-lg">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* Upsells */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="pw-upsells">
        <UpsellCard
          icon={Briefcase} title="Want your business website to stand out more?"
          subtitle="Fully Custom Website Design: ₱40,000 one-time setup"
          desc="Custom layout, unique branding, more professional appearance. Built around your specific rental business."
          ctaLabel="Request Custom Website"
          testId="pw-upsell-custom"
          done={!!data.custom_website_requested}
          status={data.custom_website_status}
          onClick={() => requestUpsell("custom_website")} />
        <UpsellCard
          icon={Star} title="Premium Visibility Add-On"
          subtitle="₱2,000 / month: priority visibility where available"
          desc="Featured placement in rental search areas where applicable. Activation is reviewed by Super Admin, not auto-enabled. We don't guarantee exact ranking or bookings."
          ctaLabel={data.premium_visibility_active ? "Active" :
                    data.premium_visibility_requested ? "Requested: pending review" :
                    "Request Premium Visibility"}
          testId="pw-upsell-premium"
          disabled={data.premium_visibility_active || data.premium_visibility_requested}
          done={!!data.premium_visibility_requested}
          onClick={() => requestUpsell("premium_visibility")} />
      </div>
    </div>
  );
}

function Card({ title, children, testId }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid={testId}>
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      {children}
    </div>
  );
}
function Text({ label, value, onChange, testId, disabled }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <input type="text" value={value} disabled={disabled} data-testid={testId}
             onChange={(e) => onChange(e.target.value)}
             className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100" />
    </label>
  );
}
function Area({ label, value, onChange, testId, disabled, rows = 3 }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">{label}</span>
      <textarea rows={rows} value={value} disabled={disabled} data-testid={testId}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100" />
    </label>
  );
}
function Toggle({ label, checked, onChange, testId, disabled }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm">
      <input type="checkbox" checked={checked} disabled={disabled} data-testid={testId}
             onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}
function UpsellCard({ icon: Icon, title, subtitle, desc, ctaLabel, onClick, testId, done, status, disabled }) {
  return (
    <div className={`rounded-xl border p-5 ${done ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"}`}
         data-testid={testId}>
      <div className="inline-flex items-center gap-2"><Icon className="w-4 h-4 text-[#1B4332]" /><span className="font-display font-semibold">{title}</span></div>
      <div className="text-sm text-slate-700 font-medium mt-1">{subtitle}</div>
      <p className="text-sm text-slate-600 mt-2">{desc}</p>
      {status && (
        <div className="text-xs text-emerald-800 mt-1" data-testid={`${testId}-status`}>Status: {status}</div>
      )}
      <button onClick={onClick} disabled={disabled} data-testid={`${testId}-btn`}
              className="mt-3 px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm hover:opacity-90 disabled:opacity-60">
        {ctaLabel}
      </button>
    </div>
  );
}

function WebsiteLockedScreen({ plan }) {
  return (
    <div className="max-w-3xl mx-auto p-6 space-y-5" data-testid="website-locked">
      <div className="rounded-2xl border-2 border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-white p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center flex-shrink-0">
            <Lock className="w-5 h-5 text-amber-700" />
          </div>
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-700">Enterprise feature</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">Public Booking Website</h1>
            <p className="text-sm text-slate-700 mt-2 max-w-2xl">
              Upgrade to Enterprise to unlock a shareable multi-page booking website inside
              ConfirmPH: your own branded mini-site with Home, Fleet, Booking, About, Policies,
              FAQ, Reviews, and Contact pages.
            </p>
            <p className="text-[11px] text-slate-500 mt-1">Your current plan: <strong className="capitalize">{plan || "free"}</strong></p>
          </div>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-slate-700" data-testid="website-locked-features">
          {[
            "Multi-page booking website inside ConfirmPH",
            "Shareable business website link with custom slug",
            "Home, Fleet, Booking, About, Policies, FAQ, Reviews, Contact",
            "Hero image + logo + theme color branding",
            "Featured vehicles on the home page",
            "Editable policies and FAQ",
            "Public vehicle detail pages with photos",
            "Powered by ConfirmPH trust / process layout",
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-2">
              <Sparkles className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" /> {f}
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <Link to="/app/billing" data-testid="website-locked-upgrade"
                className="px-5 py-2.5 rounded-lg bg-[#1B4332] text-white text-sm font-semibold hover:opacity-90">
            Upgrade to Enterprise
          </Link>
          <Link to="/businesses" data-testid="website-locked-benefits"
                className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50">
            View Enterprise benefits
          </Link>
          <Link to="/rentals/dumaguete-island-wheels/home" target="_blank" rel="noreferrer"
                data-testid="website-locked-demo"
                className="px-5 py-2.5 rounded-lg border border-slate-300 bg-white text-sm hover:bg-slate-50 inline-flex items-center gap-1">
            <ExternalLink className="w-3.5 h-3.5" /> Preview demo website
          </Link>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">
          The Website feature unlocks tooling. ConfirmPH does not promise guaranteed bookings,
          rankings, or customer volume.
        </p>
      </div>

      <ProfileHealthCard />
    </div>
  );
}

export function ProfileHealthCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/business/website/profile-health");
        if (!cancelled) setData(r.data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!data) return null;
  const tone =
    data.score >= 90 ? "bg-emerald-50 border-emerald-300 text-emerald-900" :
    data.score >= 60 ? "bg-amber-50 border-amber-300 text-amber-900" :
    "bg-rose-50 border-rose-300 text-rose-900";
  return (
    <div className={`rounded-2xl border-2 ${tone} p-5`} data-testid="profile-health-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest">Public Profile Health</div>
          <div className="text-3xl font-display font-bold mt-0.5" data-testid="profile-health-score">{data.score}%</div>
          <div className="text-xs mt-0.5">{data.label} · {data.done} / {data.total} items complete</div>
        </div>
        <Sparkles className="w-6 h-6 opacity-60" />
      </div>
      {data.missing.length > 0 && (
        <div className="mt-3 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-widest opacity-80">Missing items</div>
          <ul className="space-y-1" data-testid="profile-health-missing">
            {data.missing.slice(0, 6).map((m) => (
              <li key={m.key} className="text-sm">
                <Link to={m.href} className="hover:underline inline-flex items-center gap-1"
                      data-testid={`profile-health-link-${m.key}`}>
                  → {m.label}
                </Link>
              </li>
            ))}
          </ul>
          {!data.is_enterprise && (
            <p className="text-[11px] mt-2 opacity-80">
              Filling these in now means your future Enterprise website will start with much
              better content automatically.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

